import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  Copy,
  CheckCircle,
  Check,
  Download,
  Send,
  MoreHorizontal,
  ChevronLeft,
  ExternalLink,
  Eye,
  Info,
  Plus,
  AlertTriangle,
  Coins,
  CreditCard,
  XCircle,
} from "lucide-react";
import { fetchWithAuth } from "../utils/api";
import { stripEmoji } from '../utils/sanitizeText';
import { formatUSD, formatCrypto } from "../utils/formatAmount";
import { generateInvoicePdf } from "../utils/invoicePdf";
import Skeleton from "../components/common/Skeleton";
import { TokenChainLogo } from "../components/common/CryptoLogo";
import AssetBadge from "../components/common/AssetBadge";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Invoice status -> v2 badge tone + readable label.
const STATUS_TONE = {
  paid: "ok",
  overpaid: "ok",
  pending: "warn",
  open: "warn",
  partially_paid: "warn",
  overdue: "warn",
  cancelled: "soft",
  refunded: "soft",
  draft: "soft",
};
const STATUS_LABEL = {
  paid: "Paid",
  overpaid: "Overpaid",
  pending: "Pending",
  open: "Open",
  partially_paid: "Partially paid",
  overdue: "Overdue",
  cancelled: "Cancelled",
  refunded: "Refunded",
  draft: "Draft",
};
// Statuses that warrant a diagonal document stamp.
const STAMP_TONE = {
  paid: "ok",
  overpaid: "ok",
  overdue: "warn",
  cancelled: "danger",
  refunded: "soft",
};

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.role_id === 1 || user?.roleId === 1;

  const [invoice, setInvoice] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(null); // 'resend' | 'paid' | 'cancel' | null
  const [showMore, setShowMore] = useState(false);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [markPaidTxHash, setMarkPaidTxHash] = useState("");
  const [markPaidReason, setMarkPaidReason] = useState("");

  const generateEvents = (inv) => {
    const events = [];
    if (inv.paidAt) {
      events.push({
        type: "invoice_paid",
        message: "Invoice was paid",
        date: inv.paidAt,
      });
      events.push({
        type: "payment_received",
        message: `Payment received from ${inv.customerEmail}`,
        date: inv.paidAt,
      });
    }
    if (inv.status === "cancelled") {
      events.push({
        type: "invoice_cancelled",
        message: "Invoice was cancelled",
        date: inv.updatedAt,
      });
    }
    if (inv.status === "overdue") {
      events.push({
        type: "invoice_overdue",
        message: "Invoice became overdue",
        date: inv.dueDate,
      });
    }
    if (inv.status === "overpaid") {
      events.push({
        type: "invoice_overpaid",
        message: "Invoice was overpaid",
        date: inv.paidAt || inv.updatedAt,
      });
    }
    if (inv.finalizedAt) {
      events.push({
        type: "invoice_finalized",
        message: "Invoice finalized",
        date: inv.finalizedAt,
      });
    }
    if (inv.paymentPageUrl) {
      events.push({
        type: "payment_page_created",
        message: "Payment page created",
        date: inv.createdAt,
      });
    }
    events.push({
      type: "invoice_created",
      message: "Invoice created",
      date: inv.createdAt,
    });
    events.sort((a, b) => new Date(b.date) - new Date(a.date));
    return events;
  };

  const fetchTransactions = useCallback(async (invoiceId) => {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/transactions?invoiceId=${invoiceId}`,
      );
      const data = await response.json();
      if (data.success && data.data?.transactions)
        setTransactions(data.data.transactions);
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    }
  }, []);

  const fetchInvoice = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/invoices/${id}`);
      const data = await response.json();
      if (data.success && data.data) {
        const inv = data.data;

        let parsedLineItems = inv.lineItems;
        if (typeof parsedLineItems === "string") {
          try {
            parsedLineItems = JSON.parse(parsedLineItems);
          } catch (e) {
            parsedLineItems = [];
          }
        }
        if (!Array.isArray(parsedLineItems)) parsedLineItems = [];

        const mappedInvoice = {
          id: inv.invoiceId,
          linkId: inv.linkId,
          customer_name: inv.customerName,
          customer_email: inv.customerEmail,
          amount: parseFloat(inv.amount) || 0,
          currency: inv.currency || "USD",
          status: inv.status,
          due_date: inv.dueDate,
          created_at: inv.createdAt,
          finalized_at: inv.finalizedAt,
          paid_at: inv.paidAt,
          payment_method: inv.paymentMethod || "Crypto Payment",
          network: inv.network,
          token: inv.token,
          amount_paid: parseFloat(inv.amountPaid) || 0,
          amount_remaining: parseFloat(inv.amountRemaining) || 0,
          tx_hash: inv.txHash,
          receiving_address: inv.receivingAddress,
          description: inv.description,
          items:
            parsedLineItems.length > 0
              ? parsedLineItems.map((item) => ({
                  description: item.description,
                  quantity: item.quantity || 1,
                  price:
                    parseFloat(item.unit_price) ||
                    parseFloat(item.unitPrice) ||
                    0,
                  amount: parseFloat(item.amount) || 0,
                }))
              : [
                  {
                    description: inv.description || "Invoice Payment",
                    quantity: 1,
                    price: parseFloat(inv.amount) || 0,
                    amount: parseFloat(inv.amount) || 0,
                  },
                ],
          events: generateEvents(inv),
          metadata: inv.metadata || {},
        };
        setInvoice(mappedInvoice);
        fetchTransactions(inv.invoiceId);
      } else {
        setInvoice(null);
      }
    } catch (error) {
      console.error("Failed to fetch invoice:", error);
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  }, [id, fetchTransactions]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const getInvoiceViewUrl = () => {
    if (!invoice?.linkId) return null;
    return `${window.location.origin}/invoice/${invoice.linkId}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleCopyLink = () => {
    const url = getInvoiceViewUrl();
    if (!url) {
      toast.error("Payment link is not available for this invoice");
      return;
    }
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Payment link copied");
    setTimeout(() => setCopied(false), 1600);
  };

  // Download the PDF branded with the merchant's own logo/details. The invoice
  // API doesn't carry merchant branding, so fetch it from /user-details first.
  const handleDownloadPdf = async () => {
    setBusy("pdf");
    try {
      let merchant = { email: user?.email || "" };
      try {
        const res = await fetchWithAuth(`${API_BASE_URL}/user-details`);
        const data = await res.json();
        const b = data?.data?.businessInfo;
        const p = data?.data?.profileInfo;
        if (b) {
          // Logo fallback → company name, else the merchant's own name, else email.
          const fullName = [p?.firstName, p?.lastName]
            .filter(Boolean)
            .join(" ")
            .trim();
          merchant = {
            businessName: b.businessName || fullName || user?.email || "",
            logoUrl: b.logoUrl
              ? `${API_BASE_URL.replace("/api", "")}${b.logoUrl}`
              : "",
            website: b.website || "",
            address: b.address || "",
            phone: b.phone || "",
            email: user?.email || "",
          };
        }
      } catch (e) {
        // Fall back to a name-only merchant; the PDF still generates.
      }
      // Pass the on-chain payments so the PDF can break the invoice down per
      // transaction (asset, network, amount) instead of only summarising it.
      await generateInvoicePdf(invoice, merchant, transactions);
    } catch (error) {
      toast.error("Failed to generate PDF");
    } finally {
      setBusy(null);
    }
  };

  const handleResend = async () => {
    setBusy("resend");
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/invoices/${invoice.id}/resend`,
        { method: "POST" },
      );
      const data = await response.json();
      if (data.success) {
        toast.success(
          `Reminder sent to ${data.data?.sentTo || invoice.customer_email}`,
        );
      } else {
        toast.error(data.message || "Failed to send reminder");
      }
    } catch (error) {
      toast.error("Failed to send reminder");
    } finally {
      setBusy(null);
    }
  };

  const handleMarkPaid = async () => {
    if (!markPaidTxHash.trim()) {
      toast.error("Transaction hash is required");
      return;
    }
    if (!markPaidReason.trim()) {
      toast.error("Reason is required");
      return;
    }
    setBusy("paid");
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/invoices/${invoice.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            status: "paid",
            txHash: markPaidTxHash.trim(),
            reason: markPaidReason.trim(),
          }),
        },
      );
      const data = await response.json();
      if (data.success) {
        toast.success("Invoice marked as paid");
        setShowMarkPaidModal(false);
        setMarkPaidTxHash("");
        setMarkPaidReason("");
        fetchInvoice();
      } else {
        toast.error(data.message || "Failed to update invoice");
      }
    } catch (error) {
      toast.error("Failed to update invoice");
    } finally {
      setBusy(null);
    }
  };

  const handleCancel = async () => {
    setShowMore(false);
    setBusy("cancel");
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/invoices/${invoice.id}/cancel`,
        { method: "PATCH" },
      );
      const data = await response.json();
      if (data.success) {
        toast.success("Invoice cancelled");
        fetchInvoice();
      } else {
        toast.error(data.message || "Failed to cancel invoice");
      }
    } catch (error) {
      toast.error("Failed to cancel invoice");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="inv-v2" aria-busy="true">
        <header className="page-head">
          <div className="ph-left">
            <Skeleton variant="text" width={64} height={11} />
            <div className="ph-title" style={{ marginTop: 8 }}>
              <Skeleton variant="text" width={150} height={26} />
              <Skeleton variant="line" width={84} height={22} />
            </div>
            <div style={{ marginTop: 10 }}>
              <Skeleton variant="text" width={280} height={13} />
            </div>
          </div>
          <div className="ph-actions">
            <Skeleton variant="line" width={150} height={38} />
            <Skeleton variant="line" width={130} height={38} />
            <Skeleton variant="line" width={120} height={38} />
          </div>
        </header>

        <div className="grid">
          <aside className="rail">
            <div className="rail-sec">
              <Skeleton variant="text" width={72} height={11} />
              <div style={{ marginTop: 10 }}>
                <Skeleton variant="line" width="55%" height={38} />
              </div>
              <div style={{ marginTop: 14 }}>
                <Skeleton variant="line" width="100%" height={7} />
              </div>
            </div>
            <div className="rail-sec">
              <div className="rail-actions">
                <Skeleton variant="line" width="100%" height={38} />
                <div className="duo">
                  <Skeleton variant="line" width="100%" height={38} />
                  <Skeleton variant="line" width="100%" height={38} />
                </div>
              </div>
            </div>
            <div className="rail-sec">
              <div className="dl">
                {[0, 1, 2, 3].map((i) => (
                  <div className="row" key={i}>
                    <Skeleton variant="text" width={72} height={13} />
                    <Skeleton variant="text" width={110} height={13} />
                  </div>
                ))}
              </div>
            </div>
          </aside>

          <div className="doc-col">
            <Skeleton variant="card" width="100%" height={520} />
            <Skeleton variant="card" width="100%" height={200} />
          </div>
        </div>

        <style>{styles}</style>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="inv-v2">
        <div className="iv-state">
          <CreditCard size={44} />
          <h2>Invoice not found</h2>
          <p>This invoice doesn&apos;t exist or has been deleted.</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate("/dashboard/invoices")}
          >
            Back to invoices
          </button>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  const statusKey = String(invoice.status || "").toLowerCase();
  const tone = STATUS_TONE[statusKey] || "soft";
  const label =
    STATUS_LABEL[statusKey] ||
    (statusKey ? statusKey.replace(/_/g, " ") : "Unknown");
  const isPaid = ["paid", "overpaid"].includes(statusKey);
  const isClosed = isPaid || ["cancelled", "refunded"].includes(statusKey);
  const currency = invoice.currency || "USD";

  const collected = invoice.amount_paid || 0;
  const amountDue =
    invoice.amount_remaining > 0
      ? invoice.amount_remaining
      : isPaid
        ? 0
        : invoice.amount;
  const progressPct =
    invoice.amount > 0
      ? Math.min(100, Math.round((collected / invoice.amount) * 100))
      : 0;

  let daysPastDue = null;
  if (statusKey === "overdue" && invoice.due_date) {
    const d = Math.floor(
      (Date.now() - new Date(invoice.due_date).getTime()) / 86400000,
    );
    if (d > 0) daysPastDue = d;
  }

  const subtotal =
    invoice.metadata?.subtotal ||
    invoice.items.reduce(
      // Stored as `unitPrice`; `price` is only a fallback for older rows.
      (sum, item) => sum + (item.amount || item.quantity * (item.unitPrice ?? item.price)),
      0,
    );
  const gstAmount = invoice.metadata?.gstAmount || 0;
  const gstPct =
    invoice.metadata?.gstType === "percentage"
      ? invoice.metadata?.gstValue
      : null;

  const issuerName =
    user?.businessName ||
    user?.business_name ||
    [user?.firstName || user?.first_name, user?.lastName || user?.last_name]
      .filter(Boolean)
      .join(" ") ||
    user?.name ||
    user?.email ||
    "Your business";
  const issuerEmail = user?.email || "";

  const invoiceUrl = getInvoiceViewUrl();
  const stampTone = STAMP_TONE[statusKey];

  const Cur = ({ children }) => <span className="cur">{children}</span>;

  const eventDot = (type) => {
    if (
      ["invoice_paid", "payment_received", "invoice_finalized"].includes(type)
    )
      return "ok";
    if (["invoice_overdue", "invoice_cancelled"].includes(type)) return "warn";
    return "";
  };

  return (
    <div className="inv-v2">
      <div className="iv-crumb">
        <button
          className="back"
          onClick={() => navigate("/dashboard/invoices")}
          aria-label="Back to invoices"
        >
          <ChevronLeft size={15} />
        </button>
        <span className="lnk" onClick={() => navigate("/dashboard/invoices")}>
          Invoices
        </span>
        <span className="sep">/</span>
        <span className="cur-crumb num">{invoice.id}</span>
      </div>

      {/* Page header */}
      <header className="page-head rise">
        <div className="ph-left">
          <div className="eyebrow">Invoice</div>
          <div className="ph-title">
            <h1 className="num">{invoice.id}</h1>
            <span className={`badge badge-${tone}`}>
              <span className="bdot" />
              {label}
            </span>
          </div>
          <div className="ph-sub">
            Billed to <b>{invoice.customer_name || "—"}</b>Created{" "}
            {formatDate(invoice.created_at)}
          </div>
        </div>
        <div className="ph-actions">
          <button className="btn" onClick={handleCopyLink}>
            {copied ? <Check size={16} /> : <Copy size={16} />}
            <span>{copied ? "Copied" : "Copy payment link"}</span>
          </button>
          <button
            className="btn"
            onClick={handleDownloadPdf}
            disabled={busy === "pdf"}
          >
            <Download size={16} />
            {busy === "pdf" ? "Preparing…" : "Download PDF"}
          </button>
          {!isClosed && (
            <button
              className="btn btn-primary"
              onClick={handleResend}
              disabled={busy === "resend"}
            >
              <Send size={16} />
              {busy === "resend" ? "Sending…" : "Send reminder"}
            </button>
          )}
          <div className="more-wrap">
            <button
              className="btn btn-icon"
              aria-label="More actions"
              onClick={() => setShowMore((v) => !v)}
            >
              <MoreHorizontal size={16} />
            </button>
            {showMore && (
              <>
                <div
                  className="more-backdrop"
                  onClick={() => setShowMore(false)}
                />
                <div className="more-menu" role="menu">
                  {invoiceUrl && (
                    <a
                      className="more-item"
                      href={invoiceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      role="menuitem"
                    >
                      <Eye size={14} /> Preview customer view
                    </a>
                  )}
                  {!isClosed && (
                    <button
                      className="more-item danger"
                      role="menuitem"
                      onClick={handleCancel}
                      disabled={busy === "cancel"}
                    >
                      <XCircle size={14} /> Cancel invoice
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="grid">
        {/* Control rail */}
        <aside className="rail rise" style={{ animationDelay: ".05s" }}>
          <div className="rail-sec">
            <div className="eyebrow">Amount due</div>
            <div className="due-amount num">
              {formatUSD(amountDue)} <Cur>{currency}</Cur>
            </div>
            <div className="due-row">
              <span className={`badge badge-${tone}`}>
                <span className="bdot" />
                {label}
              </span>
              {daysPastDue != null && (
                <span className="due-late">
                  {daysPastDue} days past due · {formatDate(invoice.due_date)}
                </span>
              )}
            </div>
            <div className="progress">
              <div className="ptrack">
                <div className="pfill" style={{ width: `${progressPct}%` }} />
              </div>
              <div className="pcap">
                <span>
                  <span className="num">{formatUSD(collected)}</span> {currency}{" "}
                  collected
                </span>
                <span>
                  of <span className="num">{formatUSD(invoice.amount)}</span>{" "}
                  {currency}
                </span>
              </div>
            </div>
          </div>

          <div className="rail-sec">
            <div className="rail-actions">
              {!isClosed ? (
                <>
                  <button
                    className="btn btn-primary btn-block"
                    onClick={handleResend}
                    disabled={busy === "resend"}
                  >
                    <Send size={16} />
                    {busy === "resend" ? "Sending…" : "Send payment reminder"}
                  </button>
                  <div className="duo">
                    <button className="btn" onClick={handleCopyLink}>
                      {copied ? <Check size={16} /> : <Copy size={16} />}
                      <span>{copied ? "Copied" : "Copy link"}</span>
                    </button>
                    {isSuperAdmin && (
                      <button
                        className="btn"
                        onClick={() => setShowMarkPaidModal(true)}
                        disabled={busy === "paid"}
                      >
                        Mark as paid
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <div className="rail-closed">
                  <CheckCircle size={16} />
                  <span>
                    This invoice is {label.toLowerCase()}. No further action
                    needed.
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="rail-sec">
            <div className="dl">
              <div className="row">
                <span className="k">Customer</span>
                <span className="v">
                  {invoice.customer_name || "—"}
                  <small>{invoice.customer_email}</small>
                </span>
              </div>
              <div className="row">
                <span className="k">Due date</span>
                <span className="v">{formatDate(invoice.due_date)}</span>
              </div>
              <div className="row">
                <span className="k">Asset</span>
                <span className="v">
                  <AssetBadge
                    symbol={invoice.token}
                    chain={invoice.network}
                    size={22}
                    pendingLabel="Network & token"
                    pendingHint="selected at checkout"
                  />
                </span>
              </div>
              <div className="row">
                <span className="k">Currency</span>
                <span className="v">{currency}</span>
              </div>
            </div>
          </div>

          {invoice.linkId && (
            <div className="rail-sec">
              <div className="rail-title">Payment link</div>
              <div className="pay-link">
                <span className="lk num">
                  {invoiceUrl?.replace(/^https?:\/\//, "")}
                </span>
                <button
                  className="lk-btn"
                  onClick={handleCopyLink}
                  aria-label="Copy link"
                  title="Copy link"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
                <a
                  className="lk-btn"
                  href={invoiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open link"
                  title="Open in new tab"
                >
                  <ExternalLink size={14} />
                </a>
              </div>
              <a
                className="preview-link"
                href={invoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Eye size={13} /> Preview customer view
              </a>
              <div className="qr-wrap">
                <div className="qr">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(invoiceUrl)}`}
                    alt="QR code for invoice payment link"
                    width={108}
                    height={108}
                  />
                </div>
                <div className="qr-text">
                  <div className="qr-cap">Scan to pay</div>
                  <div className="qr-help">
                    Anyone with this link can settle the invoice on-chain.
                  </div>
                </div>
              </div>
            </div>
          )}
        </aside>

        {/* Document column */}
        <div className="doc-col">
          {/* Receipt */}
          <section
            className="invoice-doc rise"
            style={{ animationDelay: ".1s" }}
          >
            {stampTone && (
              <div className={`stamp stamp-${stampTone}`}>{label}</div>
            )}

            <div className="doc-head">
              <div className="doc-issuer">
                {/* <div className="brand-mark">M</div> */}
                <div className="name">
                  {issuerName}
                  <small>via MaxelPay · maxelpay.com</small>
                </div>
              </div>
              <div className="doc-meta">
                <div className="lbl">Invoice</div>
                <div className="no num">{invoice.id}</div>
                <div className="dates">
                  Issued <b>{formatDate(invoice.created_at)}</b>
                  <br />
                  Due <b>{formatDate(invoice.due_date)}</b>
                </div>
              </div>
            </div>

            <div className="doc-parties">
              <div className="party">
                <div className="lbl">Billed to</div>
                <div className="nm">{invoice.customer_name || "—"}</div>
                <div className="ml">{invoice.customer_email}</div>
              </div>
              <div className="party">
                <div className="lbl">From</div>
                <div className="nm">{issuerName}</div>
                <div className="ml">{issuerEmail}</div>
              </div>
            </div>

            <table className="doc-items">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="r">Qty</th>
                  <th className="r">Unit price</th>
                  <th className="r">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, i) => (
                  <tr key={i}>
                    <td className="it">
                      <b>{item.description || "Item"}</b>
                    </td>
                    <td className="r q num">{item.quantity}</td>
                    <td className="r num">
                      {formatUSD(item.unitPrice ?? item.price)} <Cur>{currency}</Cur>
                    </td>
                    <td className="r num">
                      {formatUSD(item.amount || item.quantity * (item.unitPrice ?? item.price))}{" "}
                      <Cur>{currency}</Cur>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="doc-tot-wrap">
              <div className="doc-tot">
                <div className="trow">
                  <span className="lbl">Subtotal</span>
                  <span className="val num">
                    {formatUSD(subtotal)} {currency}
                  </span>
                </div>
                <div className="trow">
                  <span className="lbl">
                    Tax{gstPct != null ? ` (${gstPct}%)` : " (0%)"}
                  </span>
                  <span className="val num">
                    {formatUSD(gstAmount)} {currency}
                  </span>
                </div>
                <div className="trow total">
                  <span className="lbl">Total</span>
                  <span className="val num">
                    {formatUSD(invoice.amount)} {currency}
                  </span>
                </div>
                <div className="trow">
                  <span className="lbl">Amount paid</span>
                  <span className="val num">
                    {formatUSD(collected)} {currency}
                  </span>
                </div>
                {isPaid ? (
                  <div className="trow due trow-ok">
                    <span className="lbl">Amount paid</span>
                    <span className="val num">
                      {formatUSD(collected)} {currency}
                    </span>
                  </div>
                ) : (
                  <div className="trow due">
                    <span className="lbl">Amount due</span>
                    <span className="val num">
                      {formatUSD(amountDue)} {currency}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="doc-terms">
              <Info size={16} />
              <div className="tt">
                {invoice.metadata?.termsConditions ? (
                  invoice.metadata.termsConditions
                ) : (
                  <>
                    <b>Payment terms.</b> Due on receipt. This invoice is
                    settled in cryptocurrency — the payer selects their
                    preferred network and token at checkout, and the equivalent
                    of {formatUSD(invoice.amount)} {currency} is collected.
                  </>
                )}
              </div>
            </div>
          </section>

          {/* Payments & transactions */}
          <section className="card rise" style={{ animationDelay: ".15s" }}>
            <div className="card-head">
              <div className="card-title">Payments &amp; transactions</div>
            </div>
            <div className="card-pad">
              <table className="ptable">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Token</th>
                    <th>From</th>
                    <th>Tx hash</th>
                    <th>Date</th>
                  </tr>
                </thead>
                {transactions.length > 0 && (
                  <tbody>
                    {transactions.map((tx, i) => (
                      <tr key={i}>
                        <td>
                          <span
                            className={`badge badge-${tx.status === "confirmed" || tx.status === "succeeded" ? "ok" : "warn"}`}
                          >
                            <span className="bdot" />
                            {tx.status || "pending"}
                          </span>
                        </td>
                        <td className="num">{formatCrypto(tx.amount || 0)}</td>
                        <td>
                          <span className="token-chip">
                            <TokenChainLogo
                              symbol={tx.currency}
                              chain={tx.network}
                              size={18}
                            />
                            <span>{tx.currency || "—"}</span>
                          </span>
                        </td>
                        <td className="num muted-cell">
                          {tx.fromAddress
                            ? `${tx.fromAddress.slice(0, 6)}…${tx.fromAddress.slice(-4)}`
                            : "—"}
                        </td>
                        <td>
                          {tx.txHash ? (
                            <a
                              className="num tx-link"
                              href={`${tx.networkUrl}/tx/${tx.txHash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {tx.txHash.slice(0, 8)}…{tx.txHash.slice(-6)}
                              <ExternalLink size={12} />
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="muted-cell">
                          {tx.createdAt ? formatDate(tx.createdAt) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
              {transactions.length === 0 && (
                <div className="empty">
                  <span className="empty-ic">
                    <Coins size={22} />
                  </span>
                  <h4>No payments received yet</h4>
                  <p>
                    When {invoice.customer_name || "the customer"} pays, the
                    on-chain transaction will appear here with its hash and
                    confirmation. Share the payment link to get this invoice
                    settled.
                  </p>
                  {invoice.linkId && (
                    <button
                      className="btn"
                      onClick={handleCopyLink}
                      style={{ margin: "0 auto" }}
                    >
                      {copied ? <Check size={16} /> : <Copy size={16} />}
                      <span>{copied ? "Copied" : "Copy payment link"}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Activity */}
          <section className="card rise" style={{ animationDelay: ".2s" }}>
            <div className="card-head">
              <div className="card-title">Activity</div>
            </div>
            <div className="card-pad" style={{ paddingTop: 14 }}>
              <div className="timeline">
                {invoice.events.map((event, i) => {
                  const dot = eventDot(event.type);
                  return (
                    <div
                      key={i}
                      className={`tl-item${dot === "warn" ? " warn" : ""}`}
                    >
                      <div className={`tl-dot${dot ? ` ${dot}` : ""}`}>
                        {dot === "ok" ? (
                          <Check size={11} />
                        ) : dot === "warn" ? (
                          <AlertTriangle size={10} />
                        ) : (
                          <Plus size={11} />
                        )}
                      </div>
                      <div className="tl-body">
                        <div className="t">{event.message}</div>
                        <div className="d">{formatDate(event.date)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </div>
      </div>

      {showMarkPaidModal && (
        <>
          <div
            className="mp-modal-overlay"
            onClick={() => {
              if (busy !== "paid") {
                setShowMarkPaidModal(false);
                setMarkPaidTxHash("");
                setMarkPaidReason("");
              }
            }}
          />
          <div
            className="mp-modal-container"
            role="dialog"
            aria-modal="true"
            aria-label="Mark invoice as paid"
          >
            <div className="mp-modal-header">
              <div className="mp-modal-icon">
                <CheckCircle size={22} />
              </div>
              <h3>Mark invoice as paid</h3>
            </div>
            <div className="mp-modal-body">
              <p>
                Record a manual settlement for <strong>{invoice?.id}</strong>.
                This action is logged for audit.
              </p>
              <div className="mp-form-group">
                <label htmlFor="mp-txhash">
                  Transaction hash <span className="mp-req">*</span>
                </label>
                <input
                  id="mp-txhash"
                  className="mp-input"
                  type="text"
                  placeholder="0x…"
                  value={markPaidTxHash}
                  onChange={(e) => setMarkPaidTxHash(stripEmoji(e.target.value))}
                  disabled={busy === "paid"}
                />
              </div>
              <div className="mp-form-group">
                <label htmlFor="mp-reason">
                  Reason <span className="mp-req">*</span>
                </label>
                <input
                  id="mp-reason"
                  className="mp-input"
                  type="text"
                  placeholder="e.g. Off-chain settlement confirmed by ops"
                  value={markPaidReason}
                  // Stripped here too: the reason is written to the audit
                  // log and read back in admin reports.
                  onChange={(e) => setMarkPaidReason(stripEmoji(e.target.value))}
                  disabled={busy === "paid"}
                />
              </div>
            </div>
            <div className="mp-modal-footer">
              <button
                className="btn"
                onClick={() => {
                  setShowMarkPaidModal(false);
                  setMarkPaidTxHash("");
                  setMarkPaidReason("");
                }}
                disabled={busy === "paid"}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleMarkPaid}
                disabled={busy === "paid"}
              >
                {busy === "paid" ? "Saving…" : "Confirm mark as paid"}
              </button>
            </div>
          </div>
        </>
      )}

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  /* The dashboard's .page-content has overflow-y:auto but no bounded height, so it
     is an inert scroll container that breaks position:sticky (the rail has zero
     sticky range and scrolls away). While this page is mounted, let the window be
     the scrollport instead so the control rail can stick. Scoped via :has() and
     removed automatically when the page unmounts. */
  .page-content:has(.inv-v2){ overflow: visible; }

  .inv-v2{
    --ink:#16151B; --ink-2:#403E49; --muted:#737080; --faint:#A3A0AC;
    --surface:#FFFFFF; --surface-2:#F3F3F6;
    --line:#ECEBF0; --line-2:#E1E0E7; --line-3:#D4D3DC;
    --brand:#4B3BD6; --brand-2:#6857E6; --brand-ink:#332793; --brand-bg:#EFEDFC;
    --warn:#A85608; --warn-strong:#8C4707; --warn-bg:#FCF2E3; --warn-line:#EFD4A6; --warn-dot:#DD7E0A;
    --ok:#0E7A52; --ok-bg:#E8F5EE; --ok-line:#C7E8D6;
    --danger:#C0392B; --danger-strong:#9E2C20; --danger-bg:#FCEEEC; --danger-line:#F0CDC8; --danger-dot:#D6452F;
    --shadow-1:0 1px 2px rgba(22,21,27,.04);
    --shadow-2:0 1px 2px rgba(22,21,27,.04), 0 2px 6px -2px rgba(22,21,27,.06);
    --shadow-doc:0 1px 3px rgba(22,21,27,.05), 0 18px 48px -28px rgba(22,21,27,.28);
    --iv-sans:'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    --iv-mono:"Geist Mono","IBM Plex Mono",ui-monospace,SFMono-Regular,Menlo,monospace;
    font-family:var(--iv-sans); color:var(--ink); font-size:15px; line-height:1.5;
    -webkit-font-smoothing:antialiased;
  }
  .inv-v2 *{ box-sizing:border-box; }
  .inv-v2 button{ font-family:inherit; cursor:pointer; }
  .inv-v2 a{ color:inherit; text-decoration:none; }
  .inv-v2 :focus-visible{ outline:2px solid var(--brand); outline-offset:2px; border-radius:6px; }
  .inv-v2 .num{ font-family:var(--iv-mono); font-variant-numeric:tabular-nums; letter-spacing:-.01em; }
  .inv-v2 .cur{ color:var(--faint); font-weight:500; font-size:.82em; }
  .inv-v2 .eyebrow{ font-size:11px; font-weight:600; letter-spacing:.09em; text-transform:uppercase; color:var(--faint); }

  .inv-v2 .iv-state{ display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:360px; gap:14px; color:var(--muted); text-align:center; }
  .inv-v2 .iv-state h2{ margin:0; color:var(--ink); }
  .inv-v2 .iv-spinner{ width:36px; height:36px; border:3px solid var(--line-2); border-top-color:var(--brand); border-radius:50%; animation:iv-spin 1s linear infinite; }
  @keyframes iv-spin{ to{ transform:rotate(360deg); } }

  /* Breadcrumb */
  .inv-v2 .iv-crumb{ display:flex; align-items:center; gap:8px; font-size:13px; color:var(--muted); margin-bottom:18px; }
  .inv-v2 .iv-crumb .back{ width:28px; height:28px; border-radius:8px; border:1px solid var(--line-2); background:var(--surface); display:grid; place-items:center; color:var(--ink-2); }
  .inv-v2 .iv-crumb .back:hover{ background:var(--surface-2); }
  .inv-v2 .iv-crumb .lnk{ cursor:pointer; }
  .inv-v2 .iv-crumb .lnk:hover{ color:var(--ink); }
  .inv-v2 .iv-crumb .sep{ color:var(--line-3); }
  .inv-v2 .iv-crumb .cur-crumb{ color:var(--ink); font-weight:600; font-family: var(--iv-sans);}

  /* Badges */
  .inv-v2 .badge{ display:inline-flex; align-items:center; gap:6px; font-size:12px; font-weight:600; padding:4px 11px 4px 9px; border-radius:999px; line-height:1; text-transform:capitalize; }
  .inv-v2 .badge .bdot{ width:7px; height:7px; border-radius:50%; }
  .inv-v2 .badge-warn{ background:var(--warn-bg); color:var(--warn); border:1px solid var(--warn-line); }
  .inv-v2 .badge-warn .bdot{ background:var(--warn-dot); box-shadow:0 0 0 3px rgba(221,126,10,.16); }
  .inv-v2 .badge-ok{ background:var(--ok-bg); color:var(--ok); border:1px solid var(--ok-line); }
  .inv-v2 .badge-ok .bdot{ background:var(--ok); }
  .inv-v2 .badge-danger{ background:var(--danger-bg); color:var(--danger); border:1px solid var(--danger-line); }
  .inv-v2 .badge-danger .bdot{ background:var(--danger-dot); }
  .inv-v2 .badge-soft{ background:var(--surface-2); color:var(--muted); border:1px solid var(--line); }
  .inv-v2 .badge-soft .bdot{ background:var(--faint); }

  /* Page header */
  .inv-v2 .page-head{ display:flex; align-items:flex-start; justify-content:space-between; gap:24px; flex-wrap:wrap; margin-bottom:24px; position:relative; z-index:9;}
  .inv-v2 .ph-left .eyebrow{ margin-bottom:6px; }
  .inv-v2 .ph-title{ display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
  .inv-v2 .ph-title h1{ margin:0; font-size:25px; font-weight:600; letter-spacing:-.015em; font-family: var(--iv-sans);}
  .inv-v2 .ph-sub{ margin-top:8px; font-size:13.5px; color:var(--muted); }
  .inv-v2 .ph-sub b{ color:var(--ink-2); font-weight:600; white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 300px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;}
  .inv-v2 .ph-actions{ display:flex; gap:9px; flex-wrap:wrap; align-items:center; }

  /* Buttons */
  .inv-v2 .btn{ width:auto; display:inline-flex; align-items:center; justify-content:center; gap:8px; font-size:13.5px; font-weight:600; padding:9px 15px; border-radius:10px; border:1px solid var(--line-2); background:var(--surface); color:var(--ink); transition:.14s; white-space:nowrap; box-shadow:var(--shadow-1); }
  .inv-v2 .btn:hover{ background:var(--surface-2); }
  .inv-v2 .btn:disabled{ opacity:.6; cursor:default; }
  .inv-v2 .btn-primary{ background:var(--brand); border-color:var(--brand); color:#fff; box-shadow:0 6px 16px -7px rgba(75,59,214,.7), inset 0 1px 0 rgba(255,255,255,.16); }
  .inv-v2 .btn-primary:hover{ background:#4031c2; }
  .inv-v2 .btn-icon{ padding:9px 11px; }
  .inv-v2 .btn-block{ width:100%; justify-content:center; }

  .inv-v2 .more-wrap{ position:relative; }
  .inv-v2 .more-backdrop{ position:fixed; inset:0; z-index:40; }
  .inv-v2 .more-menu{ position:absolute; right:0; top:calc(100% + 6px); z-index:50; min-width:210px; background:var(--surface); border:1px solid var(--line-2); border-radius:12px; box-shadow:var(--shadow-2); padding:6px; }
  .inv-v2 .more-item{ display:flex; align-items:center; gap:10px; width:100%; padding:9px 10px; border:none; background:transparent; border-radius:8px; font-size:13px; font-weight:500; color:var(--ink-2); text-align:left; }
  .inv-v2 .more-item:hover{ background:var(--surface-2); color:var(--ink); }
  .inv-v2 .more-item.danger{ color:var(--danger); }
  .inv-v2 .more-item.danger:hover{ background:var(--danger-bg); }

  /* Grid */
  .inv-v2 .grid{ display:grid; grid-template-columns:340px minmax(0,1fr); gap:24px; align-items:start; }
  .inv-v2 .doc-col{ display:flex; flex-direction:column; gap:24px; min-width:0; }

  /* Rail */
  .inv-v2 .rail{ position:sticky; top:80px; display:flex; flex-direction:column; background:var(--surface); border:1px solid var(--line); border-radius:16px; box-shadow:var(--shadow-2); overflow:hidden; }
  .inv-v2 .rail-sec{ padding:20px 22px; }
  .inv-v2 .rail-sec + .rail-sec{ border-top:1px solid var(--line); }
  .inv-v2 .due-amount{ font-size:38px; font-weight:600; letter-spacing:-.02em; line-height:1; margin-top:8px; font-family: var(--iv-sans); }
  .inv-v2 .due-amount .cur{ font-size:.46em; }
  .inv-v2 .due-row{ margin-top:12px; display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .inv-v2 .due-late{ font-size:12.5px; color:var(--warn); font-weight:600; }
  .inv-v2 .progress{ margin-top:16px; }
  .inv-v2 .ptrack{ height:7px; border-radius:99px; background:var(--surface-2); border:1px solid var(--line); overflow:hidden; }
  .inv-v2 .pfill{ height:100%; background:linear-gradient(90deg,var(--brand),var(--brand-2)); border-radius:99px; transition:width .4s ease; }
  .inv-v2 .pcap{ margin-top:8px; display:flex; justify-content:space-between; font-size:11.5px; color:var(--muted); }
  .inv-v2 .pcap .num{ color:var(--ink-2); }

  .inv-v2 .rail-actions{ display:flex; flex-direction:column; gap:8px; }
  .inv-v2 .rail-actions .duo{ display:flex; gap:8px; }
  .inv-v2 .rail-actions .duo .btn{ flex:1; justify-content:center; }
  .inv-v2 .rail-closed{ display:flex; align-items:flex-start; gap:10px; font-size:13px; color:var(--ok); }
  .inv-v2 .rail-closed svg{ flex:none; margin-top:1px; }

  .inv-v2 .dl{ display:flex; flex-direction:column; gap:13px; }
  .inv-v2 .dl .row{ display:flex; align-items:baseline; justify-content:space-between; gap:14px; }
  .inv-v2 .dl .k{ font-size:12.5px; color:var(--muted); }
  .inv-v2 .dl .v{ font-size:13px; color:var(--ink); font-weight:500; text-align:right; white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 300px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;}
  .inv-v2 .dl .v small{ display:block; color:var(--faint); font-weight:400; font-size:11.5px; }

  .inv-v2 .rail-title{ font-size:11px; font-weight:600; letter-spacing:.09em; text-transform:uppercase; color:var(--faint); margin-bottom:12px; }
  .inv-v2 .pay-link{ display:flex; align-items:center; gap:8px; border:1px solid var(--line-2); background:var(--surface-2); border-radius:10px; padding:6px 6px 6px 12px; }
  .inv-v2 .pay-link .lk{ flex:1; min-width:0; font-size:11.5px; color:var(--ink-2); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .inv-v2 .lk-btn{ width:30px; height:30px; border-radius:8px; border:1px solid var(--line-2); background:var(--surface); display:grid; place-items:center; color:var(--ink-2); flex:none; transition:.14s; }
  .inv-v2 .lk-btn:hover{ background:var(--surface-2); }
  .inv-v2 .preview-link{ display:inline-flex; align-items:center; gap:5px; margin-top:10px; font-size:12px; font-weight:600; color:var(--brand); }
  .inv-v2 .qr-wrap{ margin-top:16px; padding-top:16px; border-top:1px dashed var(--line-2); display:flex; align-items:center; gap:14px; }
  .inv-v2 .qr{ padding:8px; background:#fff; border:1px solid var(--line-2); border-radius:12px; line-height:0; flex:none; }
  .inv-v2 .qr img{ display:block; border-radius:5px; }
  .inv-v2 .qr-text .qr-cap{ font-size:12.5px; font-weight:600; }
  .inv-v2 .qr-text .qr-help{ font-size:11.5px; color:var(--faint); margin-top:3px; line-height:1.4; }

  /* Document */
  .inv-v2 .invoice-doc{ position:relative; background:var(--surface); border:1px solid var(--line-2); border-radius:16px; box-shadow:var(--shadow-doc); padding:38px 40px 34px; overflow:hidden; }
  .inv-v2 .invoice-doc::before{ content:""; position:absolute; left:0; right:0; top:0; height:3px; background:linear-gradient(90deg,var(--brand),var(--brand-2)); }
  .inv-v2 .doc-head{ display:flex; align-items:flex-start; justify-content:space-between; gap:24px; }
  .inv-v2 .doc-issuer{ display:flex; align-items:center; gap:12px; }
  .inv-v2 .brand-mark{ width:38px; height:38px; border-radius:11px; background:var(--brand); display:grid; place-items:center; color:#fff; font-weight:800; font-size:19px; box-shadow:inset 0 1px 0 rgba(255,255,255,.18), 0 4px 12px -5px rgba(75,59,214,.6); }
  .inv-v2 .doc-issuer .name{ font-size:16px; font-weight:700; letter-spacing:-.01em; }
  .inv-v2 .doc-issuer .name small{ display:block; font-weight:500; font-size:11.5px; color:var(--muted); margin-top:1px; }
  .inv-v2 .doc-meta{ text-align:right; }
  .inv-v2 .doc-meta .lbl{ font-size:11px; font-weight:600; letter-spacing:.18em; text-transform:uppercase; color:var(--brand); }
  .inv-v2 .doc-meta .no{ font-size:17px; font-weight:600; margin-top:4px; }
  .inv-v2 .doc-meta .dates{ margin-top:10px; font-size:12px; color:var(--muted); line-height:1.7; }
  .inv-v2 .doc-meta .dates b{ color:var(--ink-2); font-weight:600; }

  .inv-v2 .doc-parties{ display:grid; grid-template-columns:1fr 1fr; gap:28px; margin-top:30px; padding-top:26px; border-top:1px solid var(--line); }
  .inv-v2 .party .lbl{ font-size:10.5px; font-weight:600; letter-spacing:.08em; text-transform:uppercase; color:var(--faint); margin-bottom:7px; }
  .inv-v2 .party .nm{ font-size:15px; font-weight:600; display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;
    max-width: 500px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;}
  .inv-v2 .party .ml{ font-size:13px; color:var(--muted); margin-top:2px; }

  .inv-v2 .doc-items{ width:100%; border-collapse:collapse; margin-top:30px; }
  .inv-v2 .doc-items thead th{ font-size:10.5px; font-weight:600; letter-spacing:.07em; text-transform:uppercase; color:var(--faint); text-align:left; padding:0 0 12px; border-bottom:1px solid var(--line-2); }
  .inv-v2 .doc-items thead th.r{ text-align:right; }
  .inv-v2 .doc-items tbody td{ padding:16px 0; border-bottom:1px solid var(--line); font-size:14px; vertical-align:top; }
  .inv-v2 .doc-items td.r{ text-align:right; }
  .inv-v2 .doc-items .it b{ font-weight:600; display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;
    max-width: 500px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;}
  .inv-v2 .doc-items .q{ color:var(--muted); }

  .inv-v2 .doc-tot-wrap{ display:flex; justify-content:flex-end; margin-top:22px; }
  .inv-v2 .doc-tot{ width:312px; max-width:100%; }
  .inv-v2 .trow{ display:flex; justify-content:space-between; align-items:baseline; padding:7px 0; font-size:13.5px; }
  .inv-v2 .trow .lbl{ color:var(--muted); }
  .inv-v2 .trow .val{ color:var(--ink-2); }
  .inv-v2 .trow.total{ border-top:1px solid var(--line-2); margin-top:5px; padding-top:13px; }
  .inv-v2 .trow.total .lbl{ color:var(--ink); font-weight:700; font-size:14.5px; }
  .inv-v2 .trow.total .val{ color:var(--ink); font-weight:600; font-size:15px; }
  .inv-v2 .trow.due{ background:var(--warn-bg); border:1px solid var(--warn-line); border-radius:10px; padding:12px 14px; margin-top:11px; }
  .inv-v2 .trow.due .lbl{ color:var(--warn-strong); font-weight:700; }
  .inv-v2 .trow.due .val{ color:var(--warn-strong); font-weight:700; font-size:15px; }
  .inv-v2 .trow.due.trow-ok{ background:var(--ok-bg); border-color:var(--ok-line); }
  .inv-v2 .trow.due.trow-ok .lbl, .inv-v2 .trow.due.trow-ok .val{ color:var(--ok); }

  .inv-v2 .doc-terms{ margin-top:28px; padding-top:22px; border-top:1px solid var(--line); display:flex; gap:14px; align-items:flex-start; }
  .inv-v2 .doc-terms svg{ color:var(--faint); flex:none; margin-top:2px; }
  .inv-v2 .doc-terms .tt{ font-size:12.5px; color:var(--muted); line-height:1.6; }
  .inv-v2 .doc-terms .tt b{ color:var(--ink-2); font-weight:600; }

  .inv-v2 .stamp{ position:absolute; left:8%; top:60%; transform:rotate(-11deg); border:2.5px solid rgba(168,86,8,.30); color:rgba(168,86,8,.40); font-weight:800; font-size:25px; letter-spacing:.14em; padding:6px 16px; border-radius:9px; text-transform:uppercase; pointer-events:none; user-select:none; }
  .inv-v2 .stamp-ok{ border-color:rgba(14,122,82,.30); color:rgba(14,122,82,.42); }
  .inv-v2 .stamp-danger{ border-color:rgba(192,57,43,.30); color:rgba(192,57,43,.42); }
  .inv-v2 .stamp-soft{ border-color:rgba(115,112,128,.30); color:rgba(115,112,128,.45); }

  /* Cards */
  .inv-v2 .card{ background:var(--surface); border:1px solid var(--line); border-radius:14px; box-shadow:var(--shadow-1); }
  .inv-v2 .card-head{ padding:18px 22px 0; }
  .inv-v2 .card-title{ font-size:12px; font-weight:600; letter-spacing:.08em; text-transform:uppercase; color:var(--muted); }
  .inv-v2 .card-pad{ padding:16px 22px 22px; }

  .inv-v2 .ptable{ width:100%; border-collapse:collapse; }
  .inv-v2 .ptable thead th{ font-size:10.5px; font-weight:600; letter-spacing:.07em; text-transform:uppercase; color:var(--faint); text-align:left; padding:0 0 12px; border-bottom:1px solid var(--line); }
  .inv-v2 .ptable tbody td{ padding:14px 0; border-bottom:1px solid var(--line); font-size:13px; }
  .inv-v2 .ptable tbody tr:last-child td{ border-bottom:none; }
  .inv-v2 .muted-cell{ color:var(--muted); }
  .inv-v2 .token-chip{ display:inline-flex; align-items:center; gap:6px; padding:2px 8px; background:var(--surface-2); border-radius:6px; font-size:11.5px; font-weight:600; }
  .inv-v2 .tx-link{ display:inline-flex; align-items:center; gap:4px; color:var(--brand); }
  .inv-v2 .tx-link:hover{ text-decoration:underline; }

  .inv-v2 .empty{ text-align:center; padding:24px 16px 8px; }
  .inv-v2 .empty-ic{ width:46px; height:46px; border-radius:13px; background:var(--surface-2); border:1px solid var(--line); display:inline-grid; place-items:center; color:var(--faint); margin-bottom:14px; }
  .inv-v2 .empty h4{ margin:0 0 5px; font-size:14.5px; font-weight:600; }
  .inv-v2 .empty p{ margin:0 auto 16px; font-size:13px; color:var(--muted); max-width:360px; line-height:1.5; }

  .inv-v2 .timeline{ position:relative; padding:4px 0 0; }
  .inv-v2 .timeline::before{ content:""; position:absolute; left:9px; top:14px; bottom:14px; width:2px; background:var(--line); }
  .inv-v2 .tl-item{ position:relative; display:flex; gap:14px; padding:9px 0; }
  .inv-v2 .tl-dot{ width:20px; height:20px; border-radius:50%; flex:none; z-index:1; background:var(--surface); border:2px solid var(--line-2); display:grid; place-items:center; color:var(--faint); }
  .inv-v2 .tl-dot.warn{ background:var(--warn-bg); border-color:var(--warn-dot); color:var(--warn); }
  .inv-v2 .tl-dot.ok{ background:var(--ok-bg); border-color:var(--ok); color:var(--ok); }
  .inv-v2 .tl-body .t{ font-size:13.5px; font-weight:600; color:var(--ink); }
  .inv-v2 .tl-item.warn .tl-body .t{ color:var(--warn-strong); }
  .inv-v2 .tl-body .d{ font-size:12px; color:var(--faint); margin-top:1px; }

  /* Motion */
  .inv-v2 .rise{ opacity:1; }
  @media (prefers-reduced-motion: no-preference){
    .inv-v2 .rise{ animation:iv-rise .5s cubic-bezier(.22,.7,.25,1) forwards; }
    @keyframes iv-rise{ from{ opacity:0; transform:translateY(8px); } to{ opacity:1; transform:translateY(0); } }
  }
  @media (prefers-reduced-motion: reduce){ .inv-v2 .rise{ opacity:1; } }

  /* Responsive */
  @media (max-width:1080px){
    .inv-v2 .grid{ grid-template-columns:1fr; }
    .inv-v2 .rail{ position:static; }
  }
  @media (max-width: 610px){
  .inv-v2 .more-backdrop{position:unset;}
  .inv-v2 .more-menu{
      position:unset;margin-top:10px;
  }
  .inv-v2 .page-head .btn{width: 100%;margin-bottom: 10px;}
  .page-head .ph-actions {
    width: 100%;
    display: inline-block;
  }
  .inv-v2 .party .nm{white-space: unset;}
  }
  @media (max-width:640px){
    .inv-v2 .invoice-doc{ padding:26px 22px; }
    .inv-v2 .doc-parties{ grid-template-columns:1fr; gap:18px; }
    .inv-v2 .doc-head{ flex-direction:column; }
    .inv-v2 .doc-meta{ text-align:left; }
    .inv-v2 .due-amount{ font-size:32px; }
    .inv-v2 .ph-title h1{ font-size:22px; }
  }
  @media (max-width:395px){.inv-v2 .ptable thead th{font-size: 8px;}}

  /* Mark-as-paid modal */
  .mp-modal-overlay{ position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; animation:iv-mo-fade .2s ease; }
  @keyframes iv-mo-fade{ from{opacity:0} to{opacity:1} }
  .mp-modal-container{ position:fixed; top:50%; left:50%; transform:translate(-50%,-50%); z-index:1001; width:100%; max-width:440px; background:#fff; border-radius:16px; box-shadow:0 25px 80px rgba(0,0,0,.28); animation:iv-mo-in .2s ease; }
  @keyframes iv-mo-in{ from{opacity:0;transform:translate(-50%,-50%) scale(.96)} to{opacity:1;transform:translate(-50%,-50%) scale(1)} }
  .mp-modal-header{ display:flex; align-items:center; gap:14px; padding:22px 24px; border-bottom:1px solid var(--line); }
  .mp-modal-icon{ width:44px; height:44px; border-radius:12px; background:var(--brand-bg); color:var(--brand); display:grid; place-items:center; flex:none; }
  .mp-modal-header h3{ margin:0; font-size:16px; font-weight:700; color:var(--ink); }
  .mp-modal-body{ padding:22px 24px; display:flex; flex-direction:column; gap:16px; }
  .mp-modal-body p{ margin:0; font-size:13.5px; color:var(--ink-2); line-height:1.5; }
  .mp-form-group{ display:flex; flex-direction:column; gap:6px; }
  .mp-form-group label{ font-size:12.5px; font-weight:600; color:var(--ink-2); }
  .mp-req{ color:var(--danger); }
  .mp-input{ width:100%; padding:9px 12px; border:1px solid var(--line-2); border-radius:8px; font-size:13.5px; font-family:inherit; color:var(--ink); background:var(--surface); outline:none; }
  .mp-input:focus{ border-color:var(--brand); box-shadow:0 0 0 3px rgba(75,59,214,.12); }
  .mp-input:disabled{ opacity:.6; cursor:default; }
  .mp-modal-footer{ display:flex; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid var(--line); background:var(--surface-2); border-radius:0 0 16px 16px; }
  @media (max-width:480px){
    .mp-modal-container{ max-width:calc(100vw - 32px); }
    .party {
            overflow: scroll;
        }
  }
`;

export default InvoiceDetail;
